import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle2, ShieldCheck, FileText } from 'lucide-react';
import { secretKeyService } from '../../services/secretKeyService';
import { collectionService, RawFileToUpload } from '../../services/collectionService';
import { CaptchaData, DictionaryType } from '../../types';
import { useToast } from '../../context/ToastContext';

export const CollectionCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Form states
  const [collectionName, setCollectionName] = useState('');
  const [authorName, setAuthorName] = useState(() => secretKeyService.getUserAuthorName());
  const [selectedFiles, setSelectedFiles] = useState<RawFileToUpload[]>([]);
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');

  // Status states
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Success result
  const [createdResult, setCreatedResult] = useState<{
    collectionId: string;
    author: string;
    secretKey?: string;
  } | null>(null);

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const fetchCaptcha = async () => {
    setLoadingCaptcha(true);
    try {
      const data = await collectionService.generateCaptcha();
      setCaptcha(data);
    } catch (e) {
      console.error(e);
      setError('Không thể tải CAPTCHA.');
    } finally {
      setLoadingCaptcha(false);
    }
  };

  const inferDictionaryType = (filename: string): DictionaryType => {
    const lower = filename.toLowerCase();
    if (lower.includes('vietphrase') || lower.includes('vp')) return 'VietPhrase';
    if (lower.includes('name')) return 'Name';
    if (lower.includes('phienam') || lower.includes('pa') || lower.includes('chinesephienamwords')) return 'PhienAm';
    if (lower.includes('pronoun')) return 'Pronouns';
    if (lower.includes('rule') || lower.includes('luật nhân') || lower.includes('luatnhan') || lower.includes('luat nhan')) return 'Rule';
    return 'VietPhrase';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (files.length > 15) {
      toast.error('Tối đa được tải lên 15 file trong một bộ sưu tập.');
      return;
    }

    const newFiles: RawFileToUpload[] = [];
    const readPromises: Promise<void>[] = [];

    Array.from(files).forEach((file) => {
      if (!file.name.endsWith('.txt')) {
        toast.error(`File ${file.name} không đúng định dạng .txt`);
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`File ${file.name} quá lớn (tối đa 50MB/file)`);
        return;
      }

      const p = new Promise<void>((resolve) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          const content = evt.target?.result as string;
          newFiles.push({
            name: file.name,
            type: inferDictionaryType(file.name),
            content,
          });
          resolve();
        };
        reader.readAsText(file, 'UTF-8');
      });
      readPromises.push(p);
    });

    Promise.all(readPromises).then(() => {
      setSelectedFiles((prev) => {
        const uniqueNew = newFiles.filter(
          (nf, idx, self) => idx === self.findIndex((f) => f.name.toLowerCase() === nf.name.toLowerCase())
        );
        const filteredPrev = prev.filter(
          (p) => !uniqueNew.some((n) => n.name.toLowerCase() === p.name.toLowerCase())
        );
        return [...filteredPrev, ...uniqueNew];
      });
    });
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!collectionName.trim()) {
      setError('Vui lòng nhập Tên Bộ Sưu Tập.');
      return;
    }
    if (selectedFiles.length === 0) {
      setError('Vui lòng chọn ít nhất 1 file .txt để tải lên.');
      return;
    }
    if (!captchaAnswer.trim()) {
      setError('Vui lòng nhập câu trả lời CAPTCHA.');
      return;
    }

    setUploading(true);

    try {
      // 1. Get single user recovery code (1 user = 1 unique recovery code)
      const userSecretKey = secretKeyService.getUserRecoveryCode();
      const secretKeyHash = await secretKeyService.hashKey(userSecretKey);

      // Save user author name for future collections
      if (authorName.trim()) {
        secretKeyService.setUserAuthorName(authorName.trim());
      }

      // 2. Upload collection via service
      const res = await collectionService.uploadCollection({
        name: collectionName.trim(),
        author: authorName.trim() || undefined,
        secretKeyHash,
        captcha: {
          nonce: captcha?.nonce || '',
          questionId: captcha?.questionId || '',
          answer: captchaAnswer.trim(),
        },
        rawFiles: selectedFiles,
        onProgress: (msg) => setProgressMsg(msg),
      });

      // 3. Sync all existing user collections to this author name if set
      if (authorName.trim()) {
        try {
          await collectionService.updateAuthorName({
            secretKeyHash,
            author: authorName.trim(),
          });
        } catch (e) {
          console.error('Không thể đồng bộ tên tác giả cho các bộ sưu tập cũ:', e);
        }
      }

      // 4. Save key locally to legacy map as backup
      secretKeyService.saveKeyForCollection(res.collectionId, userSecretKey);

      toast.success('Đã đăng Bộ Sưu Tập thành công!');

      setCreatedResult({
        collectionId: res.collectionId,
        author: res.author,
        secretKey: userSecretKey,
      });
    } catch (err: any) {
      setError(err.message || 'Tải lên bộ sưu tập thất bại.');
      toast.error(err.message || 'Tải lên bộ sưu tập thất bại.');
      fetchCaptcha();
      setCaptchaAnswer('');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', paddingBottom: '2rem' }}>
      {/* Back button */}
      <button
        onClick={() => navigate('/collections')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.375rem',
          backgroundColor: '#ffffff',
          border: '1px solid var(--border-light)',
          color: 'var(--text-on-light)',
          fontWeight: 500,
          fontSize: '0.875rem',
          cursor: 'pointer',
          marginBottom: '1.25rem',
        }}
      >
        <ArrowLeft size={16} /> Quay Lại Danh Sách
      </button>

      {/* Success View */}
      {createdResult ? (
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.625rem',
            border: '1px solid var(--border-light)',
            padding: '2rem 1.5rem',
            textAlign: 'center',
          }}
        >
          <div style={{ color: '#16a34a', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
            <CheckCircle2 size={56} />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.5rem 0', color: 'var(--text-main)' }}>
            Đăng Bộ Sưu Tập Thành Công!
          </h2>
          <p style={{ fontSize: '0.9375rem', color: '#555', marginBottom: '1.5rem' }}>
            Tác giả: <strong>{createdResult.author}</strong> | ID: <code>{createdResult.collectionId}</code>
          </p>

          {/* Linked Key Info Badge */}
          <div
            style={{
              backgroundColor: '#ecfdf5',
              border: '1px solid #a7f3d0',
              borderRadius: '0.5rem',
              padding: '0.875rem 1rem',
              marginBottom: '1.25rem',
              textAlign: 'left',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#047857', fontWeight: 700, fontSize: '0.875rem' }}>
              <ShieldCheck size={18} />
              <span>Đã liên kết Mã Khôi Phục Cá Nhân</span>
            </div>
            <p style={{ fontSize: '0.8125rem', color: '#065f46', margin: '0.25rem 0 0 0' }}>
              Bộ sưu tập này đã tự động lưu dưới quyền sở hữu của thiết bị bạn.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <button
              onClick={() => navigate(`/collections/${createdResult.collectionId}`)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                borderRadius: '0.375rem',
                backgroundColor: 'var(--primary-action)',
                color: '#0f172a',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Xem Chi Tiết Bộ Sưu Tập
            </button>

            <button
              onClick={() => navigate('/collections')}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                borderRadius: '0.375rem',
                backgroundColor: '#ffffff',
                border: '1px solid var(--border-light)',
                color: 'var(--text-on-light)',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Về Danh Sách Bộ Sưu Tập
            </button>
          </div>

          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button
              onClick={() => navigate('/collections/keys')}
              style={{ fontSize: '0.75rem', color: '#0284c7', textDecoration: 'underline' }}
            >
              Xem / lưu mã QR khôi phục tại Quản lý khóa khôi phục
            </button>
          </div>
        </div>
      ) : (
        /* Create Form */
        <form
          onSubmit={handleSubmit}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.625rem',
            border: '1px solid var(--border-light)',
            padding: '1.5rem',
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 1.25rem 0', color: 'var(--text-main)' }}>
            Đăng Bộ Sưu Tập Mới
          </h2>

          {error && (
            <div
              style={{
                backgroundColor: '#fee2e2',
                color: 'var(--accent-danger)',
                padding: '0.75rem 1rem',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                marginBottom: '1.25rem',
              }}
            >
              {error}
            </div>
          )}

          {/* Collection Name */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#333', marginBottom: '0.375rem' }}>
              Tên Bộ Sưu Tập <span style={{ color: 'var(--accent-danger)' }}>*</span>
            </label>
            <input
              type="text"
              placeholder="VD: Từ điển VP vip pro"
              maxLength={100}
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              style={{
                width: '100%',
                padding: '0.625rem 0.75rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-light)',
                fontSize: '0.9375rem',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Author Name */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#333', marginBottom: '0.375rem' }}>
              Tên Người Dùng / Tác Giả
            </label>
            <input
              type="text"
              placeholder="Nhập biệt danh của bạn"
              maxLength={50}
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              style={{
                width: '100%',
                padding: '0.625rem 0.75rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-light)',
                fontSize: '0.9375rem',
                boxSizing: 'border-box',
              }}
            />
            <span style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', display: 'block' }}>
              Tên này sẽ được hiển thị công khai cùng Bộ Sưu Tập.
            </span>
          </div>

          {/* File Picker */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#333', marginBottom: '0.375rem' }}>
              Chọn các file từ điển (.txt) <span style={{ color: 'var(--accent-danger)' }}>*</span>
            </label>

            <div
              style={{
                border: '2px dashed var(--border-light)',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                textAlign: 'center',
                backgroundColor: 'var(--bg-primary)',
                cursor: 'pointer',
              }}
              onClick={() => document.getElementById('file-picker-input')?.click()}
            >
              <Upload size={32} style={{ color: 'var(--primary-action)', marginBottom: '0.5rem' }} />
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-on-light)' }}>
                Bấm để chọn file .txt
              </p>
              <span style={{ fontSize: '0.75rem', color: '#777' }}>
                Hỗ trợ VietPhrase.txt, Name.txt, PhienAm.txt, Pronouns.txt, Rule.txt (Tối đa 15 file, 50MB/file)
              </span>
              <input
                id="file-picker-input"
                type="file"
                multiple
                accept=".txt"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {selectedFiles.map((file, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'var(--bg-card)',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid var(--border-light)',
                      fontSize: '0.875rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileText size={16} />
                      <span style={{ fontWeight: 600 }}>{file.name}</span>
                      <select
                        value={file.type}
                        onChange={(e) => {
                          const newType = e.target.value as DictionaryType;
                          setSelectedFiles((prev) =>
                            prev.map((f, idx) => (idx === i ? { ...f, type: newType } : f))
                          );
                        }}
                        style={{
                          fontSize: '0.75rem',
                          backgroundColor: '#fef08a',
                          color: '#854d0e',
                          border: '1px solid #fde047',
                          borderRadius: '0.25rem',
                          padding: '0.125rem 0.375rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        <option value="VietPhrase">VietPhrase</option>
                        <option value="Name">Name</option>
                        <option value="PhienAm">PhienAm</option>
                        <option value="Pronouns">Pronouns</option>
                        <option value="Rule">Rule</option>
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveFile(i)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--accent-danger)',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      Gỡ bỏ
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* CAPTCHA */}
          <div style={{ marginBottom: '1.5rem', backgroundColor: '#f9fafb', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#333', marginBottom: '0.5rem' }}>
              Xác nhận CAPTCHA (Bắt buộc)
            </label>
            {loadingCaptcha ? (
              <span style={{ fontSize: '0.8125rem', color: '#666' }}>Đang tải CAPTCHA...</span>
            ) : (
              <div>
                <p style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0 0 0.5rem 0', fontWeight: 500 }}>
                  {captcha?.questionText}
                </p>
                <input
                  type="text"
                  placeholder="Nhập câu trả lời..."
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-light)',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={uploading}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              backgroundColor: 'var(--primary-action)',
              color: '#0f172a',
              border: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              cursor: uploading ? 'wait' : 'pointer',
              opacity: uploading ? 0.7 : 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            {uploading ? progressMsg || 'Đang tạo bộ sưu tập...' : 'Tạo bộ sưu tập'}
          </button>
        </form>
      )}
    </div>
  );
};
